import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm,
  Divider,
  Progress,
  InputNumber,
  Switch,
  Upload,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  BarChartOutlined,
} from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;

interface DialogueLine {
  id: string;
  speaker: string;
  content: string;
}

interface RewardItem {
  id: string;
  type: 'image' | 'video' | 'audio';
  url: string;
  title?: string;
}

interface ChoiceOption {
  id: string;
  label: string;
  intimacyDelta: number;
  rewards: RewardItem[];
}

interface SceneRecord {
  id: string;
  title: string;
  summary: string;
  videoUrl: string;
  imageUrl?: string;
  videoDuration: string;
  dialogues: DialogueLine[];
  choices: ChoiceOption[];
}

interface StoryRecord {
  id: string;
  title: string;
  summary: string;
  entryOptionLabel: string;
  scenes: SceneRecord[];
}

interface ScriptRecord {
  id: number;
  title: string;
  roleId: number;
  parentScriptId?: number;
  prerequisiteScriptIds: number[];
  unlockCondition: 'free' | 'vip' | 'paid';
  unlockCost?: number;
  coverImage: string;
  rating: number;
  heat: number;
  tags: string[];
  worldView: string;
  rules: string;
  summary: string;
  hook: string;
  stories: StoryRecord[];
  playCount: number;
  completionCount: number;
  status: 'active' | 'inactive';
  updatedAt: string;
}

const roleOptions = [
  { id: 1, name: '夜港歌姬' },
  { id: 2, name: '林婉清' },
  { id: 3, name: '林雨欣' },
  { id: 4, name: '苏曼柔' },
];

const placeholderImage = (text: string) =>
  `https://dummyimage.com/900x1600/1a1a1a/ffffff&text=${encodeURIComponent(text)}`;

const dialogueTemplates = [
  '夜色里，她的目光停留在你身上。',
  '你感到气氛微妙，却又不愿打破沉默。',
  '风从窗缝钻进来，故事继续向前。',
  '你想起刚才的细节，决定更谨慎一些。',
  '她的语气放软，似乎愿意分享更多。',
  '你靠近一步，听见她轻轻叹息。',
  '旁白：线索悄然浮现，局势变得更清晰。',
  '她点亮灯光，示意你继续往前。',
  '你回应她的邀请，准备迎接下一段故事。',
];

const conflictSeeds = [
  '对方突然失联',
  '线索被人抢先一步',
  '旧日承诺被提起',
  '误会在沉默中加深',
  '关键证据出现裂痕',
  '有人暗中监视',
  '时间即将耗尽',
  '对方情绪失控',
  '计划被迫改变',
  '真相逼近但代价高昂',
];

const hookSeeds = [
  '一张旧照片',
  '一段被剪辑的录音',
  '一封未寄出的信',
  '一只失踪的戒指',
  '一份被涂改的名单',
  '一个陌生的来电',
  '一条匿名短信',
  '一处被封锁的地点',
  '一个被掩盖的目击者',
  '一份只剩半页的档案',
];

const locationSeeds = [
  '雨巷尽头',
  '港口旧仓',
  '回廊灯影',
  '暗室门口',
  '书房窗边',
  '霓虹街角',
  '地下停车场',
  '旧报亭',
  '天台风口',
  '车站候车厅',
];

const goalSeeds = [
  '确认她的动机',
  '保护关键线索',
  '稳住她的情绪',
  '找到下一位证人',
  '争取更多时间',
  '获取进入权限',
  '辨别敌友',
  '恢复信任',
  '交换情报',
  '设下反制',
];

const choiceTemplates = [
  ['立刻跟她行动', '按下直觉先试探'],
  ['当场追问真相', '转身先找证人'],
  ['冒险押注她', '先留后手再决定'],
  ['快步追上去', '假装离开再回头'],
  ['正面硬碰硬', '从侧门绕进去'],
  ['信任她的判断', '坚持你自己的方案'],
  ['拿下关键物证', '换取她的坦白'],
  ['协助她撤离', '留下来收拾残局'],
  ['把线索公开', '私下继续追查'],
  ['今晚就摊牌', '留到明天再说'],
];

const buildDialogues = (
  roleName: string,
  storyId: string,
  sceneTitle: string,
  storyIndex: number
) => {
  const conflict = conflictSeeds[storyIndex % conflictSeeds.length];
  const hook = hookSeeds[storyIndex % hookSeeds.length];
  const location = locationSeeds[storyIndex % locationSeeds.length];
  const goal = goalSeeds[storyIndex % goalSeeds.length];
  const base = [
    `旁白：在${location}，你察觉到${conflict}。`,
    `${roleName}：别问太多，我只需要你相信我。`,
    '你：我需要一个理由，否则无法继续。',
    `旁白：线索指向${hook}，它可能改变局势。`,
    `${roleName}：如果你愿意帮我，我们就还能往前走。`,
    '你：那就说清楚，我们要怎么做？',
    `旁白：你们必须${goal}，否则下一步会更艰难。`,
  ];
  const filler = dialogueTemplates.map((template, index) => {
    const speaker = index % 3 === 0 ? '旁白' : index % 2 === 0 ? '你' : roleName;
    return `${speaker}：${template}`;
  });
  const merged = [...base, ...filler].slice(0, 10);
  return merged.map((content, index) => {
    const separatorIndex = content.indexOf('：');
    const speaker = separatorIndex > 0 ? content.slice(0, separatorIndex) : '旁白';
    const line = separatorIndex > 0 ? content.slice(separatorIndex + 1).trim() : content.trim();
    return {
      id: `d_${storyId}_${sceneTitle}_${index}`,
      speaker,
      content: line,
    };
  });
};

const titleSeeds = [
  '雨巷',
  '霓虹',
  '旧案',
  '回廊',
  '码头',
  '书房',
  '密语',
  '暗影',
  '回声',
  '灯影',
  '风暴',
  '夜色',
  '旧照',
  '信封',
  '港口',
  '花园',
  '誓言',
  '倾听',
  '拂晓',
  '残影',
];

const actionSeeds = [
  '初遇',
  '回声',
  '暗线',
  '试探',
  '追索',
  '拂面',
  '转折',
  '回望',
  '密谈',
  '守候',
  '引路',
  '留声',
  '交错',
  '微光',
  '剪影',
  '铺陈',
  '叙事',
  '余温',
  '回航',
  '终章',
];

const buildStoryTitle = (index: number) =>
  `${titleSeeds[index % titleSeeds.length]}${actionSeeds[(index * 3) % actionSeeds.length]}`;

const buildStories = (prefix: string, roleName: string, theme: string) =>
  Array.from({ length: 50 }).map((_, idx) => {
    const storyIndex = idx + 1;
    const storyTitle = buildStoryTitle(idx);
    const sceneTitle = `${storyTitle}·片段`;
    const conflict = conflictSeeds[storyIndex % conflictSeeds.length];
    const hook = hookSeeds[storyIndex % hookSeeds.length];
    const location = locationSeeds[storyIndex % locationSeeds.length];
    const goal = goalSeeds[storyIndex % goalSeeds.length];
    const summary = `${theme}中的第${storyIndex}个故事，冲突围绕“${conflict}”，线索来自“${hook}”。`;
    const [choiceA, choiceB] = choiceTemplates[storyIndex % choiceTemplates.length];
    return {
      id: `${prefix}_story_${storyIndex}`,
      title: storyTitle,
      summary,
      entryOptionLabel: `第${storyIndex}章 · ${storyTitle}`,
      scenes: [
        {
          id: `${prefix}_scene_${storyIndex}`,
          title: sceneTitle,
          summary: `地点：${location}。${roleName}与你面对${conflict}，必须${goal}。`,
          videoUrl: '',
          imageUrl: placeholderImage(`${storyTitle} ${storyIndex}`),
          videoDuration: '6s',
          dialogues: buildDialogues(roleName, `${prefix}_${storyIndex}`, sceneTitle, storyIndex),
          choices: [
            {
              id: `${prefix}_choice_${storyIndex}_1`,
              label: choiceA,
              intimacyDelta: 2,
              rewards: [
                {
                  id: `${prefix}_reward_${storyIndex}_1`,
                  type: 'image' as const,
                  url: placeholderImage(`奖励-${storyIndex}-1`),
                  title: '剧情奖励',
                },
              ],
            },
            {
              id: `${prefix}_choice_${storyIndex}_2`,
              label: choiceB,
              intimacyDelta: 1,
              rewards: [
                {
                  id: `${prefix}_reward_${storyIndex}_2`,
                  type: 'image' as const,
                  url: placeholderImage(`奖励-${storyIndex}-2`),
                  title: '线索奖励',
                },
              ],
            },
          ],
        },
      ],
    };
  });

const buildScript = (options: {
  id: number;
  title: string;
  roleId: number;
  coverImage: string;
  rating: number;
  heat: number;
  tags: string[];
  worldView: string;
  summary: string;
  hook: string;
  roleName: string;
  theme: string;
}): ScriptRecord => ({
  id: options.id,
  title: options.title,
  roleId: options.roleId,
  parentScriptId: undefined,
  prerequisiteScriptIds: [],
  unlockCondition: 'free',
  coverImage: options.coverImage,
  rating: options.rating,
  heat: options.heat,
  tags: options.tags,
  worldView: options.worldView,
  rules: '选择只影响奖励与亲密度，不改变预生成剧情顺序。',
  summary: options.summary,
  hook: options.hook,
  stories: buildStories(String(options.id), options.roleName, options.theme),
  playCount: 520,
  completionCount: 380,
  status: 'active',
  updatedAt: '2026-01-30 10:50:00',
});

const initialScripts: ScriptRecord[] = [
  buildScript({
    id: 101,
    title: '港城夜色',
    roleId: 1,
    coverImage: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=800',
    rating: 4.7,
    heat: 8120,
    tags: ['港风', '悬疑', '夜色'],
    worldView: '港城旧区霓虹闪烁，旧案与人心在夜色中交错。',
    summary: '你在雨巷听见她的歌声，旧案线索让你们的关系悄然靠近。',
    hook: '她提到一张旧照片，而照片上的人似乎认识你。',
    roleName: '夜港歌姬',
    theme: '港城雨夜',
  }),
  {
    id: 102,
    title: '入赘豪门：大小姐的玩物',
    roleId: 2,
    parentScriptId: undefined,
    prerequisiteScriptIds: [],
    unlockCondition: 'free',
    coverImage: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=800',
    rating: 4.9,
    heat: 110000000,
    tags: ['豪门', '新婚', '反转'],
    worldView: '入赘林家，本以为是飞上枝头变凤凰，却发现自己不过是豪门博弈中的一颗弃子。',
    summary: '新婚之夜的羞辱，是屈服还是反击？',
    hook: '林婉清伸出一只脚踩在你的胸口：“记住了，你只是条狗。”',
    rules: '选择只影响奖励与亲密度，不改变预生成剧情顺序。',
    playCount: 15600,
    completionCount: 12400,
    status: 'active',
    updatedAt: '2026-01-31 12:00:00',
    stories: [
      {
        id: 's2_1',
        title: '新婚之夜的羞辱',
        summary: '新婚当晚，林婉清让你睡地板。',
        entryOptionLabel: 'A. 新婚之夜的羞辱',
        scenes: [
          {
            id: 'sc2_1',
            title: '冷卧的试探',
            summary: '她穿着半透明的蕾丝睡裙在床上翻来覆去。',
            videoUrl: 'https://cdn.fantasygf.com/cdn-cgi/media/width=600/https://fantasygf.nyc3.cdn.digitaloceanspaces.com/0f3490b49ccb1ff5e5e58f3b7b67e6af.mp4',
            imageUrl: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=800',
            videoDuration: '0:15',
            dialogues: [
              { id: 'd2_1_1', speaker: '林婉清', content: '记住了，你只是条狗。' },
              { id: 'd2_1_2', speaker: '你', content: '（沉默地躺在地板上，看着天花板）' },
              { id: 'd2_1_3', speaker: '林婉清', content: '怎么不说话？哑巴了？过来，给我揉揉腿。' },
            ],
            choices: [
              {
                id: 'c2_1_1',
                label: '顺从地起身',
                intimacyDelta: 5,
                rewards: [{ id: 'r2_1_1', type: 'image', url: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=800', title: '婉清的足尖' }],
              },
              {
                id: 'c2_1_2',
                label: '冷淡拒绝',
                intimacyDelta: -2,
                rewards: [],
              },
            ],
          },
          {
            id: 'sc2_2',
            title: '深夜的低语',
            summary: '她突然从床上坐起，眼神复杂。',
            videoUrl: 'https://cdn.fantasygf.com/cdn-cgi/media/width=600/https://fantasygf.nyc3.cdn.digitaloceanspaces.com/58a2b5b8d8d960cd4c92d7118ea02e94.mp4',
            imageUrl: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=800',
            videoDuration: '0:12',
            dialogues: [
              { id: 'd2_2_1', speaker: '林婉清', content: '你是不是觉得我很坏？故意这么折磨你？' },
              { id: 'd2_2_2', speaker: '你', content: '你只是在害怕。' },
              { id: 'd2_2_3', speaker: '林婉清', content: '（愣了一下，冷笑）害怕？我林婉清会害怕？你太看得起自己了。' },
            ],
            choices: [
              { id: 'c2_2_1', label: '握住她的手', intimacyDelta: 15, rewards: [] },
              { id: 'c2_2_2', label: '继续睡觉', intimacyDelta: -5, rewards: [] },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 103,
    title: '豪门冷遇：冰山总裁的蔑视',
    roleId: 3,
    parentScriptId: undefined,
    prerequisiteScriptIds: [],
    unlockCondition: 'free',
    coverImage: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=800',
    rating: 4.8,
    heat: 95000000,
    tags: ['职场', '御姐', '黑丝'],
    worldView: '你被安排给林雨欣当助理。她坐在宽大的办公桌后，黑丝长腿交叠。',
    summary: '办公室里的权力博弈。',
    hook: '她冷冷地将一份文件扔在地上：“捡起来。连这点小事都做不好，林家不养废物。”',
    rules: '选择只影响奖励与亲密度。',
    playCount: 9800,
    completionCount: 7600,
    status: 'active',
    updatedAt: '2026-01-31 12:00:00',
    stories: [
      {
        id: 's3_1',
        title: '办公室的冷遇',
        summary: '林雨欣对你的能力表示怀疑。',
        entryOptionLabel: 'B. 办公室的冷遇',
        scenes: [
          {
            id: 'sc3_1',
            title: '捡起尊严',
            summary: '文件散落一地，她俯视着你。',
            videoUrl: '',
            imageUrl: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=800',
            videoDuration: '0:00',
            dialogues: [
              { id: 'd3_1_1', speaker: '林雨欣', content: '捡起来。连这点小事都做不好，林家不养废物。' },
              { id: 'd3_1_2', speaker: '你', content: '（蹲下身，一张张捡起文件）' },
              { id: 'd3_1_3', speaker: '林雨欣', content: '你的眼神很不服气？记住，在这里，我才是规则。' },
            ],
            choices: [
              {
                id: 'c3_1_1',
                label: '直视她的眼睛',
                intimacyDelta: 10,
                rewards: [{ id: 'r3_1_1', type: 'image', url: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=800', title: '雨欣的凝视' }],
              },
              {
                id: 'c3_1_2',
                label: '低头道歉',
                intimacyDelta: 2,
                rewards: [],
              },
            ],
          },
          {
            id: 'sc3_2',
            title: '咖啡的温度',
            summary: '你递上一杯咖啡，她正忙于处理文件。',
            videoUrl: '',
            imageUrl: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=800',
            videoDuration: '0:00',
            dialogues: [
              { id: 'd3_2_1', speaker: '你', content: '林总，您的咖啡。' },
              { id: 'd3_2_2', speaker: '林雨欣', content: '（头也不抬）放那吧。顺便把这份报表重做，数据太乱了。' },
              { id: 'd3_2_3', speaker: '你', content: '这是按照您的要求做的。' },
              { id: 'd3_2_4', speaker: '林雨欣', content: '（终于抬头，眼神犀利）你在质疑我的专业性？' },
            ],
            choices: [
              { id: 'c3_2_1', label: '据理力争', intimacyDelta: 8, rewards: [] },
              { id: 'c3_2_2', label: '默默拿回重做', intimacyDelta: 1, rewards: [] },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 104,
    title: '深夜惊魂：岳母的秘密',
    roleId: 4,
    parentScriptId: undefined,
    prerequisiteScriptIds: [],
    unlockCondition: 'free',
    coverImage: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=800',
    rating: 4.9,
    heat: 120000000,
    tags: ['禁忌', '悬疑', '旗袍'],
    worldView: '深夜口渴下楼，路过岳母苏曼柔的主卧，门虚掩着。',
    summary: '深夜里的异样声响。',
    hook: '你看到平日威严的主母正穿着真丝旗袍，面色潮红地靠在床头...',
    rules: '选择只影响奖励与亲密度。',
    playCount: 18900,
    completionCount: 15400,
    status: 'active',
    updatedAt: '2026-01-31 12:00:00',
    stories: [
      {
        id: 's4_1',
        title: '深夜的窥视',
        summary: '门缝里的真相。',
        entryOptionLabel: 'C. 深夜的窥视',
        scenes: [
          {
            id: 'sc4_1',
            title: '门缝后的风光',
            summary: '奇怪的水声和压抑的喘息。',
            videoUrl: '',
            imageUrl: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=800',
            videoDuration: '0:00',
            dialogues: [
              { id: 'd4_1_1', speaker: '旁白', content: '你听到里面传来压抑的喘息声和奇怪的水声。' },
              { id: 'd4_1_2', speaker: '你', content: '（心跳加速，慢慢靠近门缝）' },
              { id: 'd4_1_3', speaker: '苏曼柔', content: '（轻声呢喃）谁？谁在外面？' },
            ],
            choices: [
              {
                id: 'c4_1_1',
                label: '推门而入',
                intimacyDelta: 20,
                rewards: [{ id: 'r4_1_1', type: 'image', url: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=800', title: '曼柔的惊慌' }],
              },
              {
                id: 'c4_1_2',
                label: '屏住呼吸离开',
                intimacyDelta: 0,
                rewards: [],
              },
            ],
          },
          {
            id: 'sc4_2',
            title: '主母的威严',
            summary: '第二天早晨，苏曼柔在餐厅优雅地喝着红茶。',
            videoUrl: '',
            imageUrl: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=800',
            videoDuration: '0:00',
            dialogues: [
              { id: 'd4_2_1', speaker: '苏曼柔', content: '昨晚睡得好吗？我好像听到走廊有动静。' },
              { id: 'd4_2_2', speaker: '你', content: '可能是风吧，妈。' },
              { id: 'd4_2_3', speaker: '苏曼柔', content: '（放下茶杯，似笑非笑地看着你）是吗？林家的风，可是很喧嚣的。' },
            ],
            choices: [
              { id: 'c4_2_1', label: '试探昨晚的事', intimacyDelta: 12, rewards: [] },
              { id: 'c4_2_2', label: '低头喝粥', intimacyDelta: 3, rewards: [] },
            ],
          },
        ],
      },
    ],
  },
];

const SocialScriptManagement: React.FC = () => {
  const [scripts, setScripts] = useState<ScriptRecord[]>(initialScripts);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ScriptRecord | null>(null);
  const [form] = Form.useForm();

  const exportTemplate = () => {
    const headers = [
      'id',
      'title',
      'roleId',
      'parentScriptId',
      'prerequisiteScriptIds',
      'unlockCondition',
      'unlockCost',
      'coverImage',
      'rating',
      'heat',
      'tags',
      'worldView',
      'rules',
      'summary',
      'hook',
      'storiesJson',
      'playCount',
      'completionCount',
      'status',
      'updatedAt',
    ];
    const exampleStories = JSON.stringify([
      {
        id: 'story_1',
        title: '新婚之夜的羞辱',
        summary: '新婚当晚，她让你睡地板。',
        entryOptionLabel: 'A. 新婚之夜的羞辱',
        scenes: [
          {
            id: 'scene_1',
            title: '冷卧的试探',
            summary: '她靠近你，眼神里带着试探。',
            videoUrl: 'https://cdn.example.com/videos/a1.mp4',
            imageUrl: 'https://dummyimage.com/900x1600/1a1a1a/ffffff&text=%E5%9C%BA%E6%99%AF%E6%96%87%E6%9C%AC',
            videoDuration: '3:40',
            dialogues: [
              { id: 'dialogue_1', speaker: '女主', content: '记住你的位置。' },
              { id: 'dialogue_2', speaker: '你', content: '我只想知道真相。' },
            ],
            choices: [
              {
                id: 'choice_1',
                label: '沉默顺从',
                intimacyDelta: 1,
                rewards: [
                  { id: 'reward_1', type: 'image', url: 'https://example.com/reward/a.jpg', title: '夜色照片' },
                ],
              },
            ],
          },
        ],
      },
    ]);
    const example = [
      '101',
      '雨夜入赘记',
      '1',
      '',
      '',
      'free',
      '',
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800',
      '4.6',
      '7212',
      '豪门|雨夜|禁忌',
      '江海市的云顶圈，旧贵族与新资本交织。',
      '选择只影响奖励与亲密度，不改变预生成剧情顺序。',
      '入赘豪门，你被当作工具，雨夜里她却向你伸出手。',
      '深夜传来的异样声响意味着什么？',
      `"${exampleStories.replace(/"/g, '""')}"`,
      '520',
      '380',
      'active',
      '2026-01-30 10:50:00',
    ];
    const csv = `\ufeff${headers.join(',')}\n${example.join(',')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '剧本导入模板.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const safeJsonParse = <T,>(value: string, fallback: T): T => {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  };

  const parseCsvLine = (line: string) => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      if (char === '"') {
        const nextChar = line[i + 1];
        if (inQuotes && nextChar === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }
      if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
        continue;
      }
      current += char;
    }
    result.push(current.trim());
    return result;
  };

  const parseCsv = async (file: File) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) {
      message.warning('导入文件为空或格式不正确');
      return;
    }
    const headers = parseCsvLine(lines[0]).map((h) => h.trim());
    const rows = lines.slice(1);
    const records: ScriptRecord[] = rows.map((row, index) => {
      const columns = parseCsvLine(row);
      const map: Record<string, string> = {};
      headers.forEach((header, idx) => {
        map[header] = columns[idx] || '';
      });
      const stories = safeJsonParse<StoryRecord[]>(map.storiesJson || '[]', []);
      return {
        id: map.id ? Number(map.id) : Date.now() + index,
        title: map.title || '未命名剧本',
        roleId: Number(map.roleId || 0),
        parentScriptId: map.parentScriptId ? Number(map.parentScriptId) : undefined,
        prerequisiteScriptIds: map.prerequisiteScriptIds
          ? map.prerequisiteScriptIds.split('|').map((id) => Number(id)).filter(Boolean)
          : [],
        unlockCondition: map.unlockCondition === 'vip' ? 'vip' : map.unlockCondition === 'paid' ? 'paid' : 'free',
        unlockCost: map.unlockCost ? Number(map.unlockCost) : undefined,
        coverImage: map.coverImage || '',
        rating: Number(map.rating || 0),
        heat: Number(map.heat || 0),
        tags: map.tags ? map.tags.split('|').filter(Boolean) : [],
        worldView: map.worldView || '',
        rules: map.rules || '',
        summary: map.summary || '',
        hook: map.hook || '',
        stories,
        playCount: map.playCount ? Number(map.playCount) : 0,
        completionCount: map.completionCount ? Number(map.completionCount) : 0,
        status: map.status === 'inactive' ? 'inactive' : 'active',
        updatedAt: map.updatedAt || new Date().toLocaleString('zh-CN'),
      };
    });
    setScripts((prev) => [...records, ...prev]);
    message.success(`成功导入 ${records.length} 条剧本数据`);
    setImportModalVisible(false);
  };

  const filteredScripts = useMemo(() => {
    if (!searchText) return scripts;
    const keyword = searchText.trim();
    return scripts.filter((script) => [script.title, script.summary].some((field) => field.includes(keyword)));
  }, [scripts, searchText]);

  const stats = useMemo(() => {
    const total = scripts.length;
    const totalPlays = scripts.reduce((sum, item) => sum + item.playCount, 0);
    const totalComplete = scripts.reduce((sum, item) => sum + item.completionCount, 0);
    const completionRate = totalPlays ? Math.round((totalComplete / totalPlays) * 100) : 0;
    const storyCount = scripts.reduce((sum, item) => sum + item.stories.length, 0);
    const sceneCount = scripts.reduce((sum, item) => sum + item.stories.reduce((s, story) => s + story.scenes.length, 0), 0);
    const choiceCount = scripts.reduce(
      (sum, item) =>
        sum +
        item.stories.reduce(
          (s, story) => s + story.scenes.reduce((c, scene) => c + scene.choices.length, 0),
          0
        ),
      0
    );
    return { total, totalPlays, completionRate, storyCount, sceneCount, choiceCount };
  }, [scripts]);

  const roleNameMap = useMemo(() => {
    const map = new Map<number, string>();
    roleOptions.forEach((role) => map.set(role.id, role.name));
    return map;
  }, []);

  const handleAdd = () => {
    setSelectedRecord(null);
    form.resetFields();
    form.setFieldsValue({
      status: true,
      unlockCondition: 'free',
      rating: 0,
      heat: 0,
      stories: [],
    });
    setEditModalVisible(true);
  };

  const handleEdit = (record: ScriptRecord) => {
    setSelectedRecord(record);
    form.setFieldsValue({
      ...record,
      status: record.status === 'active',
      unlockCondition: record.unlockCondition || 'free',
    });
    setEditModalVisible(true);
  };

  const handleSave = (values: any) => {
    const payload: ScriptRecord = {
      id: selectedRecord?.id || Date.now(),
      title: values.title,
      roleId: values.roleId,
      parentScriptId: values.parentScriptId,
      prerequisiteScriptIds: values.prerequisiteScriptIds || [],
      unlockCondition: values.unlockCondition || 'free',
      unlockCost: values.unlockCondition === 'paid' ? values.unlockCost : undefined,
      coverImage: values.coverImage || '',
      rating: Number(values.rating || 0),
      heat: Number(values.heat || 0),
      tags: values.tags || [],
      worldView: values.worldView || '',
      rules: values.rules || '',
      summary: values.summary,
      hook: values.hook || '',
      stories: (values.stories || []).map((story: any, storyIdx: number) => ({
        id: story.id || `s_${Date.now()}_${storyIdx}`,
        title: story.title,
        summary: story.summary,
        entryOptionLabel: story.entryOptionLabel || `选项${storyIdx + 1}`,
        scenes: (story.scenes || []).map((scene: any, sceneIdx: number) => ({
          id: scene.id || `sc_${Date.now()}_${storyIdx}_${sceneIdx}`,
          title: scene.title,
          summary: scene.summary,
          videoUrl: scene.videoUrl,
          imageUrl: scene.imageUrl,
          videoDuration: scene.videoDuration,
          dialogues: (scene.dialogues || []).map((line: any, lineIdx: number) => ({
            id: line.id || `d_${Date.now()}_${storyIdx}_${sceneIdx}_${lineIdx}`,
            speaker: line.speaker,
            content: line.content,
          })),
          choices: (scene.choices || []).map((choice: any, choiceIdx: number) => ({
            id: choice.id || `c_${Date.now()}_${storyIdx}_${sceneIdx}_${choiceIdx}`,
            label: choice.label,
            intimacyDelta: Number(choice.intimacyDelta || 0),
            rewards: (choice.rewards || []).map((reward: any, rewardIdx: number) => ({
              id: reward.id || `r_${Date.now()}_${storyIdx}_${sceneIdx}_${choiceIdx}_${rewardIdx}`,
              type: reward.type,
              url: reward.url,
              title: reward.title,
            })),
          })),
        })),
      })),
      playCount: selectedRecord?.playCount || 0,
      completionCount: selectedRecord?.completionCount || 0,
      status: values.status ? 'active' : 'inactive',
      updatedAt: new Date().toLocaleString('zh-CN'),
    };

    if (selectedRecord) {
      setScripts((prev) => prev.map((item) => (item.id === payload.id ? payload : item)));
      message.success('剧本更新成功');
    } else {
      setScripts((prev) => [payload, ...prev]);
      message.success('剧本创建成功');
    }
    setEditModalVisible(false);
    form.resetFields();
  };

  const handleDelete = (record: ScriptRecord) => {
    setScripts((prev) => prev.filter((item) => item.id !== record.id));
    message.success(`已删除剧本 ${record.title}`);
  };

  const handleView = (record: ScriptRecord) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const columns = [
    {
      title: '剧本名称',
      dataIndex: 'title',
      key: 'title',
      render: (_: string, record: ScriptRecord) => (
        <Space>
          <FileTextOutlined />
          <div>
            <div>{record.title}</div>
            <div style={{ fontSize: 12, color: '#888' }}>{record.tags.join(' / ') || '-'}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '关联角色',
      dataIndex: 'roleId',
      key: 'roleId',
      render: (roleId: number) => roleNameMap.get(roleId) || '-',
    },
    {
      title: '故事线/场景',
      key: 'structure',
      render: (_: any, record: ScriptRecord) => {
        const storyCount = record.stories.length;
        const sceneCount = record.stories.reduce((sum, story) => sum + story.scenes.length, 0);
        return (
          <Space>
            <Tag color="blue">故事 {storyCount}</Tag>
            <Tag color="purple">场景 {sceneCount}</Tag>
          </Space>
        );
      },
    },
    {
      title: '入口选项',
      key: 'entryOptions',
      render: (_: any, record: ScriptRecord) => (
        <Space direction="vertical" size={0}>
          {record.stories.map((story) => (
            <span key={story.id}>{story.entryOptionLabel || story.title}</span>
          ))}
        </Space>
      ),
    },
    {
      title: '解锁条件',
      key: 'unlock',
      render: (_: any, record: ScriptRecord) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.unlockCondition === 'free' ? 'green' : record.unlockCondition === 'vip' ? 'gold' : 'volcano'}>
            {record.unlockCondition === 'free' ? '免费' : record.unlockCondition === 'vip' ? 'VIP' : '付费'}
          </Tag>
          {record.unlockCondition === 'paid' && <span>价格：{record.unlockCost || 0}</span>}
        </Space>
      ),
    },
    {
      title: '播放/完成',
      key: 'play',
      render: (_: any, record: ScriptRecord) => {
        const rate = record.playCount ? Math.round((record.completionCount / record.playCount) * 100) : 0;
        return (
          <Space direction="vertical" size={0}>
            <span>播放：{record.playCount}</span>
            <span>完成：{record.completionCount}</span>
            <Progress percent={rate} size="small" />
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: ScriptRecord['status']) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>{status === 'active' ? '启用' : '停用'}</Tag>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: ScriptRecord) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(record)}>
            查看
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除该剧本吗？" onConfirm={() => handleDelete(record)}>
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const storyStats = useMemo(() => {
    if (!selectedRecord) return [];
    return selectedRecord.stories.map((story) => {
      const sceneCount = story.scenes.length;
      const choiceCount = story.scenes.reduce((sum, scene) => sum + scene.choices.length, 0);
      return {
        id: story.id,
        title: story.title,
        entryOptionLabel: story.entryOptionLabel,
        sceneCount,
        choiceCount,
      };
    });
  }, [selectedRecord]);

  return (
    <div>
      <Card
        title="剧本管理"
        extra={
          <Space>
            <Input
              placeholder="搜索剧本名称/简介"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 260 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => setSearchText('')}>
              重置
            </Button>
            <Button onClick={exportTemplate}>导出模板</Button>
            <Button onClick={() => setImportModalVisible(true)}>批量导入</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增剧本
            </Button>
          </Space>
        }
      >
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="剧本总数" value={stats.total} />
          </Col>
          <Col span={6}>
            <Statistic title="故事总数" value={stats.storyCount} />
          </Col>
          <Col span={6}>
            <Statistic title="场景总数" value={stats.sceneCount} />
          </Col>
          <Col span={6}>
            <Statistic title="选择总数" value={stats.choiceCount} />
          </Col>
        </Row>
        <Divider />
        <Table
          rowKey="id"
          dataSource={filteredScripts}
          columns={columns}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{ pageSize: 8 }}
        />
      </Card>

      <Modal
        title={selectedRecord ? '编辑剧本' : '新增剧本'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => form.submit()}
        width={980}
      >
        <Form form={form} layout="vertical" onFinish={handleSave} initialValues={{ status: true, unlockCondition: 'free' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="剧本名称" name="title" rules={[{ required: true, message: '请输入剧本名称' }]}>
                <Input placeholder="如：雨夜入赘记" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="关联角色" name="roleId" rules={[{ required: true, message: '请选择角色' }]}>
                <Select placeholder="选择女主角色">
                  {roleOptions.map((role) => (
                    <Option key={role.id} value={role.id}>
                      {role.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="父级剧本" name="parentScriptId">
                <InputNumber placeholder="父剧本ID" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="前置剧本ID" name="prerequisiteScriptIds">
                <Select mode="tags" placeholder="输入剧本ID后回车">
                  {scripts.map((script) => (
                    <Option key={script.id} value={script.id}>
                      {script.id} - {script.title}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="解锁条件" name="unlockCondition">
                <Select>
                  <Option value="free">免费</Option>
                  <Option value="vip">VIP</Option>
                  <Option value="paid">付费</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="付费金额" name="unlockCost">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="仅付费模式填写" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="评分" name="rating">
                <InputNumber min={0} max={5} step={0.1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="热度" name="heat">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="封面图" name="coverImage">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="标签" name="tags">
            <Select mode="tags" placeholder="输入后回车添加标签" />
          </Form.Item>
          <Form.Item label="剧本简介" name="summary">
            <TextArea rows={2} placeholder="用于列表展示的简介" />
          </Form.Item>
          <Form.Item label="剧情钩子" name="hook">
            <TextArea rows={2} placeholder="吸引用户继续体验的悬念/冲突" />
          </Form.Item>
          <Form.Item label="世界观" name="worldView">
            <TextArea rows={2} placeholder="剧情世界观/背景设定" />
          </Form.Item>
          <Form.Item label="玩法规则" name="rules">
            <TextArea rows={2} placeholder="强调选择仅影响奖励与亲密度" />
          </Form.Item>

          <Card size="small" title="故事线配置">
            <Form.List name="stories">
              {(storyFields, { add: addStory, remove: removeStory }) => (
                <>
                  {storyFields.map((storyField) => (
                    <Card
                      key={storyField.key}
                      type="inner"
                      title="故事"
                      style={{ marginBottom: 16 }}
                      extra={
                        <Button danger onClick={() => removeStory(storyField.name)}>
                          删除故事
                        </Button>
                      }
                    >
                      <Row gutter={12} style={{ marginBottom: 12 }}>
                        <Col span={8}>
                          <Form.Item
                            {...storyField}
                            name={[storyField.name, 'title']}
                            rules={[{ required: true, message: '请输入故事名称' }]}
                          >
                            <Input placeholder="如：新婚之夜的羞辱" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item {...storyField} name={[storyField.name, 'entryOptionLabel']}>
                            <Input placeholder="入口选项文案（如：A. 新婚之夜的羞辱）" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item {...storyField} name={[storyField.name, 'summary']}>
                            <Input placeholder="故事简介" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.List name={[storyField.name, 'scenes']}>
                        {(sceneFields, { add: addScene, remove: removeScene }) => (
                          <>
                            {sceneFields.map((sceneField) => (
                              <Card
                                key={sceneField.key}
                                type="inner"
                                title="场景"
                                style={{ marginBottom: 12 }}
                                extra={
                                  <Button danger onClick={() => removeScene(sceneField.name)}>
                                    删除场景
                                  </Button>
                                }
                              >
                                <Row gutter={12}>
                                  <Col span={6}>
                                    <Form.Item
                                      {...sceneField}
                                      name={[sceneField.name, 'title']}
                                      rules={[{ required: true, message: '请输入场景名称' }]}
                                    >
                                      <Input placeholder="场景名称" />
                                    </Form.Item>
                                  </Col>
                                  <Col span={8}>
                                    <Form.Item {...sceneField} name={[sceneField.name, 'summary']}>
                                      <Input placeholder="场景简介" />
                                    </Form.Item>
                                  </Col>
                                  <Col span={6}>
                                    <Form.Item {...sceneField} name={[sceneField.name, 'videoUrl']}>
                                      <Input placeholder="视频URL" />
                                    </Form.Item>
                                  </Col>
                                  <Col span={6}>
                                    <Form.Item {...sceneField} name={[sceneField.name, 'imageUrl']}>
                                      <Input placeholder="图片URL（无视频时）" />
                                    </Form.Item>
                                  </Col>
                                  <Col span={4}>
                                    <Form.Item {...sceneField} name={[sceneField.name, 'videoDuration']}>
                                      <Input placeholder="时长" />
                                    </Form.Item>
                                  </Col>
                                </Row>

                                <Card size="small" title="对话配置" style={{ marginBottom: 12 }}>
                                  <Form.List name={[sceneField.name, 'dialogues']}>
                                    {(fields, { add, remove }) => (
                                      <>
                                        {fields.map((field) => (
                                          <Row gutter={12} key={field.key} style={{ marginBottom: 8 }}>
                                            <Col span={6}>
                                              <Form.Item
                                                {...field}
                                                name={[field.name, 'speaker']}
                                                rules={[{ required: true, message: '角色/你' }]}
                                              >
                                                <Input placeholder="女主/你" />
                                              </Form.Item>
                                            </Col>
                                            <Col span={16}>
                                              <Form.Item
                                                {...field}
                                                name={[field.name, 'content']}
                                                rules={[{ required: true, message: '请输入对话内容' }]}
                                              >
                                                <Input placeholder="对话内容" />
                                              </Form.Item>
                                            </Col>
                                            <Col span={2}>
                                              <Button danger onClick={() => remove(field.name)}>
                                                删除
                                              </Button>
                                            </Col>
                                          </Row>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                                          新增对话
                                        </Button>
                                      </>
                                    )}
                                  </Form.List>
                                </Card>

                                <Card size="small" title="选择与奖励（不影响剧情，仅影响亲密度）">
                                  <Form.List name={[sceneField.name, 'choices']}>
                                    {(choiceFields, { add, remove }) => (
                                      <>
                                        {choiceFields.map((choiceField) => (
                                          <div key={choiceField.key} style={{ marginBottom: 12 }}>
                                            <Row gutter={12}>
                                              <Col span={8}>
                                                <Form.Item
                                                  {...choiceField}
                                                  name={[choiceField.name, 'label']}
                                                  rules={[{ required: true, message: '请输入选项文案' }]}
                                                >
                                                  <Input placeholder="选项文案" />
                                                </Form.Item>
                                              </Col>
                                              <Col span={6}>
                                                <Form.Item {...choiceField} name={[choiceField.name, 'intimacyDelta']}>
                                                  <InputNumber placeholder="亲密度变化" style={{ width: '100%' }} />
                                                </Form.Item>
                                              </Col>
                                              <Col span={2}>
                                                <Button danger onClick={() => remove(choiceField.name)}>
                                                  删除
                                                </Button>
                                              </Col>
                                            </Row>
                                            <Form.List name={[choiceField.name, 'rewards']}>
                                              {(rewardFields, { add: addReward, remove: removeReward }) => (
                                                <div style={{ marginBottom: 8 }}>
                                                  {rewardFields.map((rewardField) => (
                                                    <Row gutter={12} key={rewardField.key} style={{ marginBottom: 8 }}>
                                                      <Col span={5}>
                                                        <Form.Item {...rewardField} name={[rewardField.name, 'type']}>
                                                          <Select placeholder="类型">
                                                            <Option value="image">图片</Option>
                                                            <Option value="video">视频</Option>
                                                            <Option value="audio">音频</Option>
                                                          </Select>
                                                        </Form.Item>
                                                      </Col>
                                                      <Col span={11}>
                                                        <Form.Item {...rewardField} name={[rewardField.name, 'url']}>
                                                          <Input placeholder="素材URL" />
                                                        </Form.Item>
                                                      </Col>
                                                      <Col span={6}>
                                                        <Form.Item {...rewardField} name={[rewardField.name, 'title']}>
                                                          <Input placeholder="奖励名称" />
                                                        </Form.Item>
                                                      </Col>
                                                      <Col span={2}>
                                                        <Button danger onClick={() => removeReward(rewardField.name)}>
                                                          删除
                                                        </Button>
                                                      </Col>
                                                    </Row>
                                                  ))}
                                                  <Button type="dashed" onClick={() => addReward()} icon={<PlusOutlined />}>
                                                    新增奖励
                                                  </Button>
                                                </div>
                                              )}
                                            </Form.List>
                                          </div>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                                          新增选择
                                        </Button>
                                      </>
                                    )}
                                  </Form.List>
                                </Card>
                              </Card>
                            ))}
                            <Button type="dashed" onClick={() => addScene()} icon={<PlusOutlined />}>
                              新增场景
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={() => addStory()} icon={<PlusOutlined />}>
                    新增故事线
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
          <Form.Item label="启用状态" name="status" valuePropName="checked" style={{ marginTop: 16 }}>
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="剧本详情与统计"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={980}
      >
        {selectedRecord && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card size="small" title="基础信息">
              <Row gutter={16}>
                <Col span={12}>剧本名称：{selectedRecord.title}</Col>
                <Col span={12}>关联角色：{roleNameMap.get(selectedRecord.roleId)}</Col>
                <Col span={12}>父级剧本：{selectedRecord.parentScriptId || '-'}</Col>
                <Col span={12}>
                  前置剧本：{selectedRecord.prerequisiteScriptIds.length ? selectedRecord.prerequisiteScriptIds.join(', ') : '无'}
                </Col>
                <Col span={12}>评分：{selectedRecord.rating || 0}</Col>
                <Col span={12}>热度：{selectedRecord.heat || 0}</Col>
                <Col span={24}>简介：{selectedRecord.summary}</Col>
                <Col span={24}>剧情钩子：{selectedRecord.hook || '-'}</Col>
                <Col span={24}>世界观：{selectedRecord.worldView || '-'}</Col>
                <Col span={24}>玩法规则：{selectedRecord.rules || '-'}</Col>
                <Col span={12}>
                  解锁条件：
                  {selectedRecord.unlockCondition === 'free'
                    ? '免费'
                    : selectedRecord.unlockCondition === 'vip'
                    ? 'VIP'
                    : '付费'}
                </Col>
                <Col span={12}>
                  付费金额：{selectedRecord.unlockCondition === 'paid' ? selectedRecord.unlockCost || 0 : '-'}
                </Col>
              </Row>
            </Card>

            <Card size="small" title="播放统计" extra={<BarChartOutlined />}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic title="播放次数" value={selectedRecord.playCount} />
                </Col>
                <Col span={8}>
                  <Statistic title="完成人数" value={selectedRecord.completionCount} />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="完成率"
                    value={
                      selectedRecord.playCount
                        ? `${Math.round((selectedRecord.completionCount / selectedRecord.playCount) * 100)}%`
                        : '0%'
                    }
                  />
                </Col>
              </Row>
            </Card>

            <Card size="small" title="故事线结构">
              <Table
                rowKey="id"
                dataSource={storyStats}
                pagination={false}
                columns={[
                  { title: '故事名称', dataIndex: 'title', key: 'title' },
                  { title: '入口选项', dataIndex: 'entryOptionLabel', key: 'entryOptionLabel' },
                  { title: '场景数', dataIndex: 'sceneCount', key: 'sceneCount' },
                  { title: '选择数', dataIndex: 'choiceCount', key: 'choiceCount' },
                ]}
              />
            </Card>

            <Card size="small" title="场景对话预览" extra={<PlayCircleOutlined />}>
              <div style={{ 
                width: '100%', 
                background: '#000', 
                borderRadius: 8, 
                overflow: 'hidden',
                position: 'relative',
                minHeight: 500
              }}>
                {selectedRecord.stories.map((story) => (
                  <div key={story.id}>
                    {story.scenes.map((scene) => {
                      // 根据角色ID呈现不同的交互模式
                      if (selectedRecord.roleId === 2) {
                        // V1: 沉浸式全屏视频 (林婉清)
                        return (
                          <div key={scene.id} style={{ position: 'relative', height: 600 }}>
                            <video 
                              src={scene.videoUrl || 'https://cdn.fantasygf.com/cdn-cgi/media/width=600/https://fantasygf.nyc3.cdn.digitaloceanspaces.com/0f3490b49ccb1ff5e5e58f3b7b67e6af.mp4'} 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              autoPlay loop muted 
                            />
                            <div style={{ 
                              position: 'absolute', 
                              bottom: 0, 
                              left: 0, 
                              right: 0, 
                              padding: 20, 
                              background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                              color: '#fff'
                            }}>
                              <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 8 }}>{scene.title}</div>
                              {scene.dialogues.map(d => (
                                <div key={d.id} style={{ marginBottom: 4 }}>
                                  <span style={{ color: '#ff4d4f' }}>{d.speaker}: </span>
                                  <span>{d.content}</span>
                                </div>
                              ))}
                              <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                                {scene.choices.map(c => (
                                  <Button key={c.id} ghost size="small" style={{ borderRadius: 20 }}>{c.label}</Button>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      } else if (selectedRecord.roleId === 3) {
                        // V2: 社交动态流 (林雨欣) - 非全屏视频/图片
                        return (
                          <div key={scene.id} style={{ background: '#f0f2f5', height: 600, display: 'flex', flexDirection: 'column' }}>
                            <div style={{ height: '40%', background: '#000' }}>
                              <img src={scene.imageUrl} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                            <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
                              {scene.dialogues.map(d => (
                                <div key={d.id} style={{ 
                                  display: 'flex', 
                                  justifyContent: d.speaker === '你' ? 'flex-end' : 'flex-start',
                                  marginBottom: 12 
                                }}>
                                  <div style={{ 
                                    maxWidth: '80%', 
                                    padding: '8px 12px', 
                                    borderRadius: 12,
                                    background: d.speaker === '你' ? '#1890ff' : '#fff',
                                    color: d.speaker === '你' ? '#fff' : '#000',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}>
                                    <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>{d.speaker}</div>
                                    <div>{d.content}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div style={{ padding: 12, background: '#fff', borderTop: '1px solid #ddd' }}>
                              <Space wrap>
                                {scene.choices.map(c => (
                                  <Button key={c.id} type="primary" shape="round" size="small">{c.label}</Button>
                                ))}
                              </Space>
                            </div>
                          </div>
                        );
                      } else {
                        // V3: 互动剧场 (苏曼柔) - 分层展示
                        return (
                          <div key={scene.id} style={{ 
                            height: 600, 
                            backgroundImage: `url(${scene.imageUrl})`, 
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'center',
                            padding: 20
                          }}>
                            <div style={{ 
                              width: '100%', 
                              background: 'rgba(255,255,255,0.9)', 
                              borderRadius: 12, 
                              padding: 20,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                            }}>
                              <div style={{ color: '#888', fontSize: 12, marginBottom: 8 }}>{scene.title}</div>
                              <div style={{ fontSize: 16, marginBottom: 16, color: '#333', lineHeight: 1.6 }}>
                                {scene.summary}
                              </div>
                              <Divider style={{ margin: '12px 0' }} />
                              <Space direction="vertical" style={{ width: '100%' }}>
                                {scene.choices.map(c => (
                                  <Button key={c.id} block style={{ textAlign: 'left', height: 'auto', padding: '10px 15px' }}>
                                    {c.label}
                                  </Button>
                                ))}
                              </Space>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                ))}
              </div>
            </Card>
          </Space>
        )}
      </Modal>

      <Modal
        title="批量导入剧本"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={720}
      >
        <Alert
          type="info"
          showIcon
          message="上传CSV文件，字段用英文逗号分隔；列表字段用 | 连接。故事线请使用JSON字符串。"
          description="unlockCondition: free/vip/paid。storiesJson 包含 scenes/choices/dialogues，可选 imageUrl 作为无视频时封面。建议先导出模板。"
          style={{ marginBottom: 16 }}
        />
        <Upload.Dragger
          name="file"
          accept=".csv"
          multiple={false}
          beforeUpload={(file) => {
            parseCsv(file);
            return false;
          }}
          showUploadList={false}
        >
          <p className="ant-upload-drag-icon">
            <FileTextOutlined />
          </p>
          <p className="ant-upload-text">点击或拖拽CSV文件到此处导入</p>
          <p className="ant-upload-hint">建议先导出模板，按模板填写后上传</p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
};

export default SocialScriptManagement;
